import { useRef } from "react";
import { useCatalogLayoutTransition } from "@/features/catalog/hooks/useCatalogLayoutTransition";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import { resolveCatalogFontScaleValue } from "@/features/catalog/utils/catalogAppearance";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { useComponentNode } from "@/shared/ui/component-hierarchy/hooks";

interface MobileCatalogGridLayoutProps {
  columns: number;
  children: React.ReactNode;
  reserveFooterSpace?: boolean;
}

/** スマホ版カタログ形式の一覧で共有するグリッド外枠。 */
export function MobileCatalogGridLayout({
  columns,
  children,
  reserveFooterSpace = true,
}: MobileCatalogGridLayoutProps): React.ReactElement {
  // 文字倍率はカタログ・過去ログ・履歴で共通の外観設定なので、
  // 呼び出し元ごとにpropを通さずここで直接読む。
  const fontScale = useCatalogStore((state) => state.catalogFontScale);
  const node = useComponentNode();

  return (
    <ComponentScope node={node}>
      <MobileCatalogGridLayoutView
        columns={columns}
        fontScale={fontScale}
        reserveFooterSpace={reserveFooterSpace}
      >
        {children}
      </MobileCatalogGridLayoutView>
    </ComponentScope>
  );
}

type MobileCatalogGridLayoutViewProps = MobileCatalogGridLayoutProps & {
  readonly fontScale: number;
};

export function MobileCatalogGridLayoutView({
  columns,
  children,
  fontScale,
  reserveFooterSpace = true,
}: MobileCatalogGridLayoutViewProps): React.ReactElement {
  const gridRef = useRef<HTMLElement>(null);
  useCatalogLayoutTransition(gridRef);

  return (
    <section
      ref={gridRef}
      aria-label="スレッドカタログ"
      className={`grid items-start gap-2 p-2 ${reserveFooterSpace ? "pb-20" : "pb-2"} grid-flow-dense`}
      style={
        {
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          "--catalog-font-scale": resolveCatalogFontScaleValue(fontScale),
        } as React.CSSProperties
      }
    >
      {children}
    </section>
  );
}
